export const solveTriangleCircle = <
  T extends {
    x: number;
    y: number;
  },
>(
  A: T,
  B: T,
  C: T,
  E: T,
) => {
  // Calculate the circumcenter (O) for triangle ABC
  const D = 2 * (A.x * (B.y - C.y) + B.x * (C.y - A.y) + C.x * (A.y - B.y));

  if (D === 0) {
    console.warn(
      'The triangle is degenerate; cannot compute the circumcenter.',
    );
    return null;
  }

  const Ux =
    ((A.x ** 2 + A.y ** 2) * (B.y - C.y) +
      (B.x ** 2 + B.y ** 2) * (C.y - A.y) +
      (C.x ** 2 + C.y ** 2) * (A.y - B.y)) /
    D;
  const Uy =
    ((A.x ** 2 + A.y ** 2) * (C.x - B.x) +
      (B.x ** 2 + B.y ** 2) * (A.x - C.x) +
      (C.x ** 2 + C.y ** 2) * (B.x - A.x)) /
    D;
  const O = { x: Ux, y: Uy };

  // Calculate the radius of the circumcircle
  const R = Math.sqrt((A.x - O.x) ** 2 + (A.y - O.y) ** 2);

  // Compute the central angle (sector angle)
  const OA = { x: A.x - O.x, y: A.y - O.y };
  const OB = { x: B.x - O.x, y: B.y - O.y };
  const dot = OA.x * OB.x + OA.y * OB.y;
  const theta = Math.acos(dot / (R * R)); // angle in radians

  // Compute the intersection point with chord AB
  const dxOE = O.x - E.x;
  const dyOE = O.y - E.y;
  const dxAB = B.x - A.x;
  const dyAB = B.y - A.y;
  const denom = dxOE * dyAB - dyOE * dxAB;

  if (denom === 0) {
    throw new Error(
      'The line EO and the chord AB are parallel; intersection cannot be determined.',
    );
  }

  const t = ((A.x - E.x) * dyAB - (A.y - E.y) * dxAB) / denom;
  const p = {
    x: E.x + t * dxOE,
    y: E.y + t * dyOE,
  };

  // Compute intersections of EO with the circle
  const A1 = dxOE ** 2 + dyOE ** 2;
  const B1 = 2 * (dxOE * (E.x - O.x) + dyOE * (E.y - O.y));
  const C1 = (E.x - O.x) ** 2 + (E.y - O.y) ** 2 - R ** 2;
  const D1 = B1 ** 2 - 4 * A1 * C1;

  let closestPoint: T | null = null;
  let isOnRadiusBetweenAB = false;

  if (D1 >= 0) {
    const t1 = (-B1 + Math.sqrt(D1)) / (2 * A1);
    const t2 = (-B1 - Math.sqrt(D1)) / (2 * A1);

    const intersection1 = { x: E.x + t1 * dxOE, y: E.y + t1 * dyOE };
    const intersection2 = { x: E.x + t2 * dxOE, y: E.y + t2 * dyOE };

    // Choose the closest intersection by comparing distances to E
    const d1 = Math.hypot(intersection1.x - E.x, intersection1.y - E.y);
    const d2 = Math.hypot(intersection2.x - E.x, intersection2.y - E.y);

    closestPoint = (d1 < d2 ? intersection1 : intersection2) as T;

    // Verify if intersection lies inside the sector
    // We need to check if the point is between the angles formed by OA and OB
    const OI = { x: closestPoint.x - O.x, y: closestPoint.y - O.y };

    // Calculate the angle between OA and OI
    const dotAOI = OA.x * OI.x + OA.y * OI.y;
    const crossAOI = OA.x * OI.y - OA.y * OI.x;
    const angleAOI = Math.atan2(crossAOI, dotAOI);

    // Calculate the angle between OA and OB
    const crossAOB = OA.x * OB.y - OA.y * OB.x;
    const angleAOB = Math.atan2(crossAOB, dot);

    // Check if OI is between OA and OB
    isOnRadiusBetweenAB =
      (angleAOB >= 0 && angleAOI >= 0 && angleAOI <= angleAOB) ||
      (angleAOB <= 0 && angleAOI <= 0 && angleAOI >= angleAOB);
  }

  if (!isOnRadiusBetweenAB || closestPoint === null) {
    // Select closest endpoint (A or B) if intersection is outside the sector
    const ae = Math.hypot(A.x - E.x, A.y - E.y);
    const be = Math.hypot(B.x - E.x, B.y - E.y);
    closestPoint = ae < be ? A : B;
  }

  return {
    center: O,
    radius: R,
    chordAngleRadians: theta,
    chordAngleDegrees: theta * (180 / Math.PI),
    intersection: p,
    closestPoint,
  };
};
